import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { a as createLogger, d as isNumber, x as preventDefault } from './utils.js';
import { d as defineCustomElement$7 } from './cl-button2.js';
import { d as defineCustomElement$6 } from './cl-dropdown2.js';
import { d as defineCustomElement$5 } from './cl-dropdown-item2.js';
import { d as defineCustomElement$4 } from './cl-icon2.js';
import { d as defineCustomElement$3 } from './cl-scroll-wrapper2.js';
import { d as defineCustomElement$2 } from './cl-typography2.js';

// Constants
const component = 'cl-tabs';

const clTabsCss = ".tabs.sc-cl-tabs-h{border-bottom:var(--border-width) solid transparent;display:flex;position:relative}.tabs--border.sc-cl-tabs-h{border-bottom:var(--border-width) solid rgba(var(--color-neutral-200))}.tabs__container.sc-cl-tabs{display:flex;margin-bottom:calc(-1 * (var(--spacing-quarter) + var(--spacing-eighth)));padding-bottom:var(--spacing-quarter);position:relative}.tabs__dropdown.sc-cl-tabs cl-dropdown.sc-cl-tabs{--dropdown-width:fit-content;min-width:calc(10 * var(--spacing-1));top:calc(100% - var(--spacing-1))}.tabs__dropdown__button.sc-cl-tabs{--button-background-color:transparent;margin:0}.tabs__dropdown__button--active.sc-cl-tabs .content.sc-cl-tabs{--typography-color:rgba(var(--color-brand-main))}.tabs__dropdown__button--active.sc-cl-tabs .content.sc-cl-tabs cl-icon.sc-cl-tabs{--icon-color:rgba(var(--color-brand-main))}.tabs__dropdown__button.sc-cl-tabs:hover{--button-background-color:transparent}.tabs__dropdown__button.sc-cl-tabs:hover .content.sc-cl-tabs{--typography-color:rgba(var(--color-brand-main))}.tabs__dropdown__button.sc-cl-tabs:hover .content.sc-cl-tabs cl-icon.sc-cl-tabs{--icon-color:rgba(var(--color-brand-main))}.tabs__dropdown__button.sc-cl-tabs .content.sc-cl-tabs{--typography-color:rgb(var(--font-color-onlight-default));display:flex;justify-content:space-around}.tabs__dropdown__button.sc-cl-tabs .content.sc-cl-tabs cl-icon.sc-cl-tabs{--icon-color:rgb(var(--font-color-onlight-default));margin-left:calc(var(--spacing-1) + var(--spacing-half))}.tabs__dropdown__button__wrapper.sc-cl-tabs{height:100%;position:relative}.tab__indicator.sc-cl-tabs{background:rgba(var(--color-brand-600));border-radius:var(--border-radius-interactive-small);bottom:0;height:var(--spacing-half);margin:auto;position:absolute;transform-origin:center;transition:width 250ms ease-in-out, left 250ms ease-in-out;width:0}.tab__indicator--disabled.sc-cl-tabs{background:rgba(var(--color-neutral-300))}";

const ClTabs$1 = /*@__PURE__*/ proxyCustomElement(class ClTabs extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Resizes the active tab indicator when tabs are added or removed
     */
    this.mutationObserver = new MutationObserver(() => this.handleTabResize());
    /**
     * Resizes the active tab indicator when the active tab or active tab parent size changes
     */
    this.resizeObserver = new ResizeObserver(() => this.handleTabResize());
    /**
     * Handles moving the position of the tab-indicator to the current active tab
     */
    this.moveTabIndicator = (tab) => {
      let parentElement = null;
      if (tab) {
        if (this.tabBarTabs.includes(tab)) {
          parentElement = tab;
        }
        else if (this.dropdownTabs.includes(tab)) {
          parentElement = this.tabsDropdownWrapper;
        }
      }
      if (parentElement) {
        this.activeTabParent = parentElement;
      }
    };
    /**
     * Handles updating the size of the active tab indicator when the tab or tab parent size changes.
     */
    this.handleTabResize = () => {
      const margin = 'calc(var(--spacing-4) - var(--spacing-half))';
      const parentElement = this.activeTabParent;
      if (parentElement && this.activeTabIndicator) {
        this.activeTabIndicator.style.width = `calc(${parentElement.clientWidth}px - ${margin})`;
        this.activeTabIndicator.style.left = `${parentElement.offsetLeft}px`;
        this.activeTabIndicator.style.marginLeft = `calc(${margin} / 2)`;
      }
    };
    /**
     * Handles setting the indices of nested cl-tab components
     */
    this.setTabIndices = (tabs) => {
      tabs.forEach((tab, index) => {
        var _a;
        const tabIndex = (_a = tab.attributes.getNamedItem('index')) === null || _a === void 0 ? void 0 : _a.value;
        if (!tabIndex) {
          tab.setAttribute('index', `${index}`);
        }
      });
    };
    /**
     * Handles closing the dropdown when clicking anywhere on the document
     */
    this.handleAllPageClicks = (event) => {
      const target = event.target;
      // Prevents the dropdown from closing when clicking on the dropdown button to open the dropdown
      if (this.moreButton && this.dropdown && !this.moreButton.contains(target) && !this.dropdown.contains(target)) {
        this.dropdownOpen = false;
      }
    };
    /**
     * Handles opening/collapsing the dropdown when clicking the dropdown button
     */
    this.handleToggleDropdown = () => {
      this.dropdownOpen = !this.dropdownOpen;
    };
    /**
     * Handles setting the active state of a tab inside the dropdown
     */
    this.getSetDropdownItemActive = (tab) => function setDropdownItemActive() {
      tab.setAttribute('active', 'true');
    };
    this.activeTabParent = undefined;
    this.dropdownOpen = false;
    this.dropdownItemIsSelected = false;
    this.dropdownTabs = [];
    this.tabBarTabs = [];
    this.tabs = undefined;
    this.border = false;
    this.dropdownLabel = '';
    this.maxTabs = 7;
  }
  /**
   * Handles adding and removing a resizeObserver to the current active tab parent element
   */
  handleActiveTabParent(activeTabParent, previousActiveTabParent) {
    if (previousActiveTabParent) {
      this.resizeObserver.unobserve(previousActiveTabParent);
    }
    if (activeTabParent) {
      this.resizeObserver.observe(activeTabParent);
    }
  }
  /**
   * Handles adding/removing eventlisteners to the document to listen for clicks
   */
  handleDropdownOpen(dropdownOpen) {
    const eventListener = document[dropdownOpen ? 'addEventListener' : 'removeEventListener'];
    eventListener('click', this.handleAllPageClicks);
  }
  /**
   * Handles setting the tab-indices and hiding/showing tabs based on the maxTabs
   */
  handleTabs(tabs) {
    this.setTabIndices(tabs);
    this.handleMaxTabs(this.maxTabs);
  }
  /**
   * Validates the maxTabs property and updates the dropdownTabs-array
   */
  handleMaxTabs(maxTabs) {
    if (!maxTabs && !isNumber(maxTabs)) {
      this.logger.error.attribute.maxTabs(`Given invalid value "${maxTabs}".`);
      this.maxTabs = 7;
      return;
    }
    this.dropdownTabs = this.tabs.filter((tab) => {
      var _a;
      const index = (_a = tab.attributes.getNamedItem('index')) === null || _a === void 0 ? void 0 : _a.value;
      const isDropdownTab = !!(index && +index > this.maxTabs - 1);
      tab.classList.toggle('tab--hidden', isDropdownTab);
      return isDropdownTab;
    });
    this.tabBarTabs = this.tabs.filter(tab => !this.dropdownTabs.includes(tab));
    const selectedDropdownItem = this.tabs.find(tab => tab === null || tab === void 0 ? void 0 : tab.className.includes('tab--active'));
    if (selectedDropdownItem) {
      this.dropdownItemIsSelected = this.dropdownTabs.includes(selectedDropdownItem);
    }
  }
  /**
   * Handles changing the active style of the "More"-button
   */
  handleTabSelected(event) {
    if (event.target.nodeName === 'CL-TAB') {
      const tab = event.target;
      this.dropdownOpen = false;
      this.dropdownItemIsSelected = this.dropdownTabs.includes(tab);
      this.moveTabIndicator(tab);
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Map all the slotted child-elements;
   */
  connectedCallback() {
    this.tabs = Array.from(this.element.children).filter(i => i.nodeName === 'CL-TAB');
  }
  /**
   * Every time the component is disconnected from the DOM:
   * - Disconnect the resize observer;
   * - Disconnect the mutation observer;
   * - Removes click eventListener when the component
   */
  disconnectedCallback() {
    this.resizeObserver.disconnect();
    this.mutationObserver.disconnect();
    document.removeEventListener('click', this.handleAllPageClicks);
  }
  /**
   * Just once after the component has fully loaded and rendered:
   * - Add a mutation observer to the tabs container element;
   * - Add a resize observer to the tabs container element;
   */
  componentDidLoad() {
    if (this.tabsContainer) {
      this.mutationObserver.observe(this.tabsContainer, { childList: true });
      this.resizeObserver.observe(this.tabsContainer);
    }
  }
  /**
   * After every render:
   * - Move the tab-indicator to the correct position;
   */
  componentDidRender() {
    this.moveTabIndicator(this.tabs.find(el => el.className.includes('tab--active')));
  }
  render() {
    const { border, dropdownLabel, maxTabs } = this;
    const showDropdown = typeof maxTabs === 'number' && this.tabs.length > maxTabs && this.dropdownTabs.length > 0;
    return (h(Host, { class: cl('tabs', { 'tabs--border': border }) }, h("div", { ref: el => {
        this.tabsContainer = el;
      }, class: 'tabs__container' }, h("slot", null), showDropdown && (h("div", { ref: el => {
        this.tabsDropdownWrapper = el;
      }, class: 'tabs__dropdown' }, h("div", { class: 'tabs__dropdown__button__wrapper' }, h("cl-button", { ref: el => {
        this.moreButton = el;
      }, class: cl('tabs__dropdown__button', {
        'tabs__dropdown__button--active': this.dropdownItemIsSelected,
      }), variant: 'text', onClick: this.handleToggleDropdown, onMouseDown: preventDefault }, h("div", { class: 'content' }, h("cl-typography", null, dropdownLabel), h("cl-icon", { name: 'micro--chevron--down', size: 2 }))), h("div", { class: cl('tab__indicator', { 'tab__indicator--hide': !this.dropdownItemIsSelected }) })), h("cl-dropdown", { ref: el => {
        this.dropdown = el;
      }, "max-height": '30', open: this.dropdownOpen }, this.dropdownOpen &&
      this.dropdownTabs.map((tab) => (h("cl-dropdown-item", { disabled: tab.className.includes('tab--disabled'), label: tab.innerText, selected: tab.className.includes('tab--active'), tabIndex: tab.tabIndex, value: tab.innerText, onClick: this.getSetDropdownItemActive(tab) })))))), h("div", { ref: el => {
        this.activeTabIndicator = el;
      }, class: cl('tab__indicator') }))));
  }
  get element() { return this; }
  static get watchers() { return {
    "activeTabParent": ["handleActiveTabParent"],
    "dropdownOpen": ["handleDropdownOpen"],
    "tabs": ["handleTabs"],
    "maxTabs": ["handleMaxTabs"]
  }; }
  static get style() { return clTabsCss; }
}, [6, "cl-tabs", {
    "border": [4],
    "dropdownLabel": [1, "dropdown-label"],
    "maxTabs": [1026, "max-tabs"],
    "activeTabParent": [32],
    "dropdownOpen": [32],
    "dropdownItemIsSelected": [32],
    "dropdownTabs": [32],
    "tabBarTabs": [32],
    "tabs": [32]
  }, [[16, "clSelect", "handleTabSelected"]]]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-tabs", "cl-button", "cl-dropdown", "cl-dropdown-item", "cl-icon", "cl-scroll-wrapper", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-tabs":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClTabs$1);
      }
      break;
    case "cl-button":
      if (!customElements.get(tagName)) {
        defineCustomElement$7();
      }
      break;
    case "cl-dropdown":
      if (!customElements.get(tagName)) {
        defineCustomElement$6();
      }
      break;
    case "cl-dropdown-item":
      if (!customElements.get(tagName)) {
        defineCustomElement$5();
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$4();
      }
      break;
    case "cl-scroll-wrapper":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const ClTabs = ClTabs$1;
const defineCustomElement = defineCustomElement$1;

export { ClTabs, defineCustomElement };

//# sourceMappingURL=cl-tabs.js.map